<template>
    <!-- An operator that asks for nothing ("est vide") draws no control at all. -->
    <template v-if="shape === 'range'">
        <div class="filter-range">
            <b-input
                :type="qualifier.type === 'date' ? 'date' : 'text'"
                :model-value="rangeFrom"
                :size="size"
                :placeholder="$t('From')"
                @update:model-value="value => emitRange(value, rangeTo)"
            />
            <span class="filter-range-sep">→</span>
            <b-input
                :type="qualifier.type === 'date' ? 'date' : 'text'"
                :model-value="rangeTo"
                :size="size"
                :placeholder="$t('To')"
                @update:model-value="value => emitRange(rangeFrom, value)"
            />
        </div>
    </template>

    <!-- A list of values: each one picked or typed, and shown as a removable tag. -->
    <template v-else-if="shape === 'list'">
        <b-taginput
            v-if="qualifier.type === 'relation' || (qualifier.enum_values || []).length > 0"
            :model-value="listTags"
            :data="listOptions"
            :size="size"
            field="label"
            autocomplete
            open-on-focus
            append-to-body
            :placeholder="$t('Add a value')"
            @typing="loadListOptions"
            @add="addListValue"
            @remove="removeListValue"
        />
        <b-taginput
            v-else
            :model-value="listValues"
            :size="size"
            :placeholder="$t('Add a value')"
            @update:model-value="values => emit('update:modelValue', joinValue(values))"
        />
    </template>

    <template v-else-if="needsValue">
        <!-- A set of known values: pick one -->
        <b-select
            v-if="qualifier.type === 'enum' || qualifier.type === 'boolean'"
            :model-value="stringValue"
            :size="size"
            expanded
            @update:model-value="value => emit('update:modelValue', value)"
        >
            <option value="">{{ $t('All') }}</option>
            <option v-for="option in qualifier.enum_values || []" :key="option.value" :value="String(option.value)">
                {{ option.label }}
            </option>
        </b-select>

        <!-- A record: searched by name, never by id -->
        <b-autocomplete
            v-else-if="qualifier.type === 'relation'"
            :model-value="relationLabel"
            :data="options"
            :placeholder="$t('Search...')"
            field="label"
            :size="size"
            icon="magnify"
            open-on-focus
            append-to-body
            clearable
            @typing="loadOptions"
            @focus="loadOptions('')"
            @select="select"
        >
            <template #empty>{{ $t('No results found') }}</template>
        </b-autocomplete>

        <!-- A day. The operator lives outside the control, so the native picker
             never sees the ">=" that used to blank it out. -->
        <b-input
            v-else-if="qualifier.type === 'date'"
            type="date"
            :model-value="stringValue"
            :size="size"
            @update:model-value="value => emit('update:modelValue', value)"
        />

        <b-input
            v-else
            :model-value="stringValue"
            :placeholder="placeholder"
            :size="size"
            @update:model-value="value => emit('update:modelValue', value)"
        />
    </template>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useQualifierValues } from '@/composables/useQualifierValues';
import { joinValue, splitValue, takesValue, valueShapeOf } from '@/utils/filterOperators';

/**
 * The value half of a filter, in the shape its type deserves.
 *
 * One control, used by the "Filter by" menu, the search bar and the advanced
 * query builder, so that picking a client is the same gesture wherever it is
 * asked — and so a new type is taught once instead of three times.
 *
 * It owns nothing but its own lookups: the operator is chosen elsewhere, and the
 * parent decides when a change reaches the URL (a typed value is usually
 * debounced, a picked one is not).
 */
const props = defineProps({
    // Qualifier descriptor from `search/qualifiers`.
    qualifier: {
        type: Object,
        required: true
    },
    // The operator in force — it decides whether a value is asked for at all.
    operator: {
        type: String,
        default: null
    },
    // The value alone, without the operator prefix the URL carries.
    modelValue: {
        type: [String, Number],
        default: ''
    },
    size: {
        type: String,
        default: 'is-small'
    },
    // Shown in the free-text control; defaults to the filter's own name.
    placeholder: {
        type: String,
        default: null
    }
});

const emit = defineEmits(['update:modelValue']);

const { optionsFor, labelFor, cachedLabel, rememberLabel } = useQualifierValues();

const options = ref([]);
const listOptions = ref([]);
const relationLabel = ref('');

const stringValue = computed(() =>
    props.modelValue === null || props.modelValue === undefined ? '' : String(props.modelValue)
);

const needsValue = computed(() => takesValue(props.qualifier, props.operator));

// none | single | list | range — declared by the operator, never guessed here.
const shape = computed(() => valueShapeOf(props.qualifier, props.operator));

const listValues = computed(() => splitValue(stringValue.value));

/** The chosen values as tags, named rather than numbered. */
const listTags = computed(() =>
    listValues.value.map(value => ({
        id: value,
        label: labelOf(value)
    }))
);

const rangeFrom = computed(() => splitValue(stringValue.value, '..')[0] ?? '');
const rangeTo = computed(() => splitValue(stringValue.value, '..')[1] ?? '');

function labelOf(value) {
    if (props.qualifier.type === 'relation') {
        return cachedLabel(props.qualifier, value) || value;
    }

    return (props.qualifier.enum_values || []).find(option => String(option.value) === value)?.label ?? value;
}

function emitRange(from, to) {
    emit('update:modelValue', from || to ? `${from ?? ''}..${to ?? ''}` : '');
}

async function loadListOptions(partial) {
    if (props.qualifier.type === 'relation') {
        listOptions.value = (await optionsFor(props.qualifier, partial)).map(option => ({
            id: String(option.id),
            label: option.label
        }));

        return;
    }

    const needle = String(partial ?? '').toLowerCase();

    listOptions.value = (props.qualifier.enum_values || [])
        .filter(option => option.label.toLowerCase().includes(needle))
        .map(option => ({ id: String(option.value), label: option.label }));
}

function addListValue(option) {
    const value = typeof option === 'object' ? String(option.id) : String(option);

    if (typeof option === 'object') {
        rememberLabel(props.qualifier, value, option.label);
    }

    if (listValues.value.includes(value)) {
        return;
    }

    emit('update:modelValue', joinValue([...listValues.value, value]));
}

function removeListValue(option) {
    const value = typeof option === 'object' ? String(option.id) : String(option);

    emit('update:modelValue', joinValue(listValues.value.filter(kept => kept !== value)));
}

const placeholder = computed(
    () => props.placeholder ?? (props.qualifier.type === 'number' ? '0' : props.qualifier.label)
);

async function loadOptions(partial) {
    options.value = await optionsFor(props.qualifier, partial);
}

function select(option) {
    if (!option) {
        return;
    }

    rememberLabel(props.qualifier, option.id, option.label);
    relationLabel.value = option.label;
    emit('update:modelValue', String(option.id));
}

/**
 * An id already in the URL must read as a name — a filter showing "1234" tells
 * nobody what is being filtered on.
 */
async function syncRelationLabel() {
    if (props.qualifier.type !== 'relation') {
        return;
    }

    if (!stringValue.value) {
        relationLabel.value = '';

        return;
    }

    relationLabel.value = cachedLabel(props.qualifier, stringValue.value) || '';
    relationLabel.value = (await labelFor(props.qualifier, stringValue.value)) || relationLabel.value;
}

watch(() => [props.qualifier.key, stringValue.value], syncRelationLabel, { immediate: true });

// The tag input has no focus event of its own, so its choices are ready before
// it is opened rather than after the first keystroke — and the records already
// chosen are named, so a query arriving from a URL does not read "1, 2".
watch(
    () => [shape.value, props.qualifier.key, stringValue.value],
    () => {
        if (shape.value !== 'list') {
            return;
        }

        loadListOptions('');

        if (props.qualifier.type === 'relation') {
            listValues.value.forEach(value => labelFor(props.qualifier, value));
        }
    },
    { immediate: true }
);
</script>

<style scoped>
.filter-range {
    display: flex;
    align-items: center;
    gap: 0.35rem;
}
.filter-range-sep {
    color: var(--bulma-text-weak);
}
</style>
